/**
 * The behaviour between the Auth0 SDK and the identity store.
 *
 * The client is an argument, so all of this is testable with a fake and none of it needs a browser.
 * ./index.js builds the real one and does nothing else.
 *
 * TWO RULES CARRIED OVER FROM WHAT THIS REPLACES
 * ----------------------------------------------
 * **`login_required` is not a logout.** It says only *this browser sent no Auth0 session cookie*.
 * `auth0-login.php` acted on it anyway — clearing ten cookies, setting a 300s tombstone and
 * broadcasting a sign-out to every other tab — which signed out fans whose token was alive, every
 * time on `localhost` and on any browser partitioning that cookie. Here it settles "nobody is
 * signed in in this browser", which is what the answer actually means, and destroys nothing: there
 * is no cookie to clear and the SDK's own cache is left alone.
 *
 * **The session check asks for the loyalty audience, and this reverses a rule taken from
 * `outlier-stats` — measured, not assumed.** That repository sets no `audience` on its
 * `Auth0Provider` and passes one per call, on the grounds that a global audience changes what the
 * silent session check requests, so a client not authorised for that API would break sign-in
 * app-wide (`src/contexts/LoyaltyClientProvider.jsx`). Sound reasoning, and it does not survive
 * contact with the SDK's cache.
 *
 * The SDK keys its cache by `client_id::audience::scope`. Resolving the session with **no**
 * audience caches an entry under `::default::` — observed in `localStorage` after a real login —
 * and the refresh token lives *in that entry*. A later request for the loyalty audience is a
 * different key, so it misses, finds no refresh token under the key it wants, and falls back to the
 * `prompt=none` iframe. On `localhost` that iframe is cross-site to the Auth0 custom domain and can
 * never succeed, so the loyalty token was structurally unobtainable: the fan resolved, and
 * `getAccessToken()` answered `login_required` forever.
 *
 * One audience, one cache entry, one refresh token, and every later read is a cache hit.
 *
 * **What the reversed rule was protecting is kept**, because it is the half that matters: a loyalty
 * misconfiguration must not cost the site its sign-in. So a failure that is *not* a no-session
 * answer is retried once with no audience, which resolves identity anyway — the header works, the
 * loyalty surfaces degrade — and says so. An unconfigured `audience` sends none, exactly as before.
 */

import { toAuthState } from './state.js';
import { safeReturnTo } from './returnTo.js';

/**
 * Auth0 errors that mean "nobody is signed in", as opposed to "something went wrong".
 *
 * `missing_refresh_token` belongs here and is easy to misread as a fault: with
 * `useRefreshTokens` the SDK raises it when its cache holds no refresh token for the requested
 * audience, which for a signed-out visitor is simply the normal state.
 */
export const NO_SESSION_ERRORS = Object.freeze( [
	'login_required',
	'consent_required',
	'interaction_required',
	'missing_refresh_token',
] );

/**
 * Whether an error means "not signed in" rather than "broken".
 *
 * The SDK puts the code on `error`; some paths carry it on `error_description` only, and a plain
 * `Error` carries neither. Anything unrecognised is treated as a real failure, because reporting a
 * broken tenant as a clean sign-out is how the old probe's bug stayed invisible for so long.
 *
 * @param {*} error Whatever was thrown.
 * @return {boolean} True when it just means nobody is signed in.
 */
export function isNoSession( error ) {
	const code = error && typeof error.error === 'string' ? error.error : '';

	return NO_SESSION_ERRORS.includes( code );
}

/**
 * Wire an SDK client to an identity store.
 *
 * @param {Object}   options            Inputs.
 * @param {Object}   options.client     An `@auth0/auth0-spa-js` client, or anything client-shaped.
 * @param {Object}   options.store      From `createAuthStore()`.
 * @param {Object}   options.config     From `readConfig()`.
 * @param {Function} [options.navigate] How to leave the page. Defaults to a no-op.
 * @param {Function} [options.warn]     Where to report a real failure.
 * @param {Function} [options.log]      Verbose step logging, on only when debugging.
 * @return {Object} The runtime.
 */
export function createAuthRuntime( {
	client,
	store,
	config,
	navigate = () => {},
	warn = () => {},
	log = () => {},
} ) {
	/**
	 * One attempt at proving a session, for a given audience.
	 *
	 * @param {string} audience The audience to request, or '' for none.
	 * @return {Promise<{ok: boolean, error?: *}>} Whether it worked.
	 */
	async function attemptSession( audience ) {
		try {
			await client.getTokenSilently(
				audience ? { authorizationParams: { audience } } : undefined
			);
			log( 'resolve: got a token', { audience: audience || '(none)' } );

			return { ok: true };
		} catch ( error ) {
			log( 'resolve: no token', {
				audience: audience || '(none)',
				error: error && error.error,
				description: error && error.error_description,
				noSession: isNoSession( error ),
			} );

			return { ok: false, error };
		}
	}

	/**
	 * Ask the SDK who this is and tell the store.
	 *
	 * Called once at startup and again whenever the browser suggests the answer may have moved —
	 * a tab regaining focus, a bfcache restore, another tab reporting a change. Idempotent: the
	 * store announces nothing when the answer has not changed.
	 *
	 * @return {Promise<Object>} The settled state.
	 */
	// One resolve at a time. The browser fires several of the cues below in quick succession -- a
	// fresh load raises `pageshow` as well as running the initial resolve, and clicking into the
	// window adds `focus` -- and each concurrent call is a separate cache lookup and, on a cold or
	// expired cache, a separate round trip. Collapsing them costs one variable.
	let inFlight = null;

	async function resolveOnce() {
		log( 'resolve: asking for a session', {
			cacheLocation: config.cacheLocation,
			useRefreshTokens: config.useRefreshTokens,
			audience: config.audience || '(none)',
		} );

		// getTokenSilently rather than isAuthenticated: it proves the session AND warms the cache,
		// so the first loyalty read does not pay for a second round trip. WITH the audience, so the
		// entry it warms is the one that will be asked for -- see the module docblock.
		let established = await attemptSession( config.audience );

		if ( ! established.ok && config.audience && ! isNoSession( established.error ) ) {
			// The loyalty audience is refused for a reason that is not "nobody is signed in" -- an
			// unauthorised client, a renamed API. Identity must still resolve: losing the crest and
			// the loyalty surfaces is a degradation, losing sign-in is an outage.
			warn( '[auth] the audience was refused, resolving identity without it', {
				audience: config.audience,
				error: established.error && established.error.error,
				description: established.error && established.error.error_description,
			} );

			established = await attemptSession( '' );
		}

		if ( ! established.ok ) {
			if ( ! isNoSession( established.error ) ) {
				// A real failure. Reported, and still settled as "nobody" -- a surface that cannot
				// establish who the fan is must not reveal anything -- but never confused with a
				// sign-out, and nothing is cleared on the strength of it.
				warn( '[auth] could not establish a session', established.error );
			}

			return store.settle( null );
		}

		let claims = null;

		try {
			claims = await client.getIdTokenClaims();
		} catch ( error ) {
			warn( '[auth] could not read the ID token claims', error );
		}

		return store.settle( toAuthState( claims ), claims );
	}

	/**
	 * Ask the SDK who this is, collapsing concurrent callers onto one attempt.
	 *
	 * @return {Promise<Object>} The settled state.
	 */
	function resolve() {
		if ( inFlight ) {
			return inFlight;
		}

		inFlight = resolveOnce().finally( () => {
			inFlight = null;
		} );

		return inFlight;
	}

	/**
	 * A bearer for one audience, or null.
	 *
	 * Null rather than a rejected promise: every caller of this is a surface deciding whether to
	 * reveal something, and a rejection there reads as a bug in the surface.
	 *
	 * @param {string} [audience] Defaults to the configured loyalty audience.
	 * @return {Promise<string|null>} The token.
	 */
	async function getAccessToken( audience = config.audience ) {
		if ( ! audience ) {
			return null;
		}

		log( 'getAccessToken: asking', { audience } );

		try {
			const token = await client.getTokenSilently( { authorizationParams: { audience } } );

			log( 'getAccessToken: got one', { audience } );

			return token;
		} catch ( error ) {
			// **A no-session code is only harmless when nobody is signed in.** If the store says
			// somebody IS, and we still cannot get a token for this audience, that is the split
			// state D-046 describes -- the site reports the fan as signed in while every loyalty
			// call fails -- and it is a fault however Auth0 labels it. The commonest cause is the
			// client not being authorised for the audience, which Auth0 reports as
			// `login_required` from a silent call and which would otherwise be swallowed here.
			//
			// This is why the first live login on the new flow looked like a success: the fan
			// resolved, and the one thing that did not work said nothing at all.
			const settled = store.state();

			if ( ! isNoSession( error ) || ( settled && settled.signedIn ) ) {
				warn( '[auth] signed in but could not obtain an access token', {
					audience,
					error: error && error.error,
					description: error && error.error_description,
				} );
			}

			return null;
		}
	}

	/**
	 * The raw ID token, or null.
	 *
	 * **A documented accessor, because the alternative was a caller reading `claims().__raw`.**
	 * That field is declared on the SDK's `IdToken` type and does hold the raw JWT, but it is not
	 * part of this contract -- a consumer depending on it would be depending on the shape of the
	 * SDK's own object, and `store.claims()` hands out a shallow copy of exactly that object for a
	 * different purpose (telling "this fan has no club" from "the tenant does not put `team_name`
	 * on the token").
	 *
	 * **Why anything wants the ID token at all.** It is the token addressed to *us* -- its `aud` is
	 * our own client id -- so it is the right thing to present to this site's own endpoints. An
	 * access token is addressed to the 3Halves loyalty API, and presenting one to WordPress is
	 * audience confusion: see [D-068](../../../../docs/decisions.md). The loyalty consent endpoint
	 * is the caller.
	 *
	 * **It asks for a token first, and that is the point rather than belt-and-braces.**
	 * `getIdTokenClaims()` reads whatever the SDK has cached and will happily hand back an EXPIRED
	 * ID token; `getTokenSilently()` is what refreshes the cache entry, and the ID token is
	 * refreshed with it. Reading the claims without that would send a dead token to an endpoint
	 * that verifies `exp`, which arrives as a 401 and reads as a broken session. On any ordinary
	 * page the extra call is a cache hit.
	 *
	 * @return {Promise<string|null>} The raw JWT, or null.
	 */
	async function getIdToken() {
		try {
			await client.getTokenSilently(
				config.audience ? { authorizationParams: { audience: config.audience } } : undefined
			);
		} catch ( error ) {
			// A refusal here is not fatal on its own: the cache may still hold a live ID token,
			// and the claim read below is what decides. It IS worth saying, because the commonest
			// cause is the audience being refused -- the split state D-046 describes.
			log( 'getIdToken: could not refresh', {
				error: error && error.error,
				noSession: isNoSession( error ),
			} );
		}

		try {
			const claims = await client.getIdTokenClaims();

			return claims && typeof claims.__raw === 'string' ? claims.__raw : null;
		} catch ( error ) {
			warn( '[auth] could not read the ID token', error );

			return null;
		}
	}

	/**
	 * Start an interactive login.
	 *
	 * `returnTo` travels in `appState`, which the SDK keeps in its own storage — so it never
	 * touches a query string and needs none of the `auth0_login_return_to` cookie machinery.
	 *
	 * @param {string} [returnTo] Where to land afterwards. Validated against the site's own origin.
	 * @return {Promise<void>}
	 */
	function login( returnTo ) {
		// **The audience has to be here too, not only on resolve().** The code exchange mints for
		// whatever `/authorize` asked for, so a login without it caches under `::default::` and
		// the very first loyalty read is a cache miss with no refresh token -- the failure this
		// pair of changes exists to remove. Asking on both means the login produces exactly the
		// one entry every later read wants.
		//
		// This is the one place the `outlier-stats` warning genuinely bites, and it has no
		// fallback: if the client were not authorised for the audience, `/authorize` answers
		// `access_denied` and the fan cannot sign in at all. Measured against this tenant before
		// relying on it -- `XvbBVaP0…` with the loyalty audience redirects to Universal Login,
		// while a non-existent audience answers `access_denied`, so the check discriminates and
		// this client passes it. Re-measure before pointing an install at a different tenant.
		const authorizationParams = { redirect_uri: config.callbackUrl };

		if ( config.audience ) {
			authorizationParams.audience = config.audience;
		}

		return client.loginWithRedirect( {
			appState: { returnTo: safeReturnTo( returnTo, config.homeUrl ) },
			authorizationParams,
		} );
	}

	/**
	 * Finish an interactive login and leave the callback page.
	 *
	 * **`appState.returnTo` is re-validated here even though the SDK stored it.** It is read back
	 * out of browser storage, and a value that came from storage is input. The cost of trusting it
	 * is an open redirect on the one URL guaranteed to be visited mid-login.
	 *
	 * @return {Promise<string>} Where the fan was sent.
	 */
	async function handleCallback() {
		let appState = null;

		try {
			( { appState } = await client.handleRedirectCallback() );
		} catch ( error ) {
			warn( '[auth] the login callback failed', error );
		}

		const target =
			safeReturnTo( appState && appState.returnTo, config.homeUrl ) || config.homeUrl;

		await resolve();
		navigate( target );

		return target;
	}

	/**
	 * Sign out, here and at Auth0.
	 *
	 * The SDK clears its own cache and then leaves for the tenant's end-session endpoint, so there
	 * is no list of cookies to keep in step and no tombstone to expire. `markLoggedOut()` and
	 * `auth0_logged_out` exist only because the old design could not do either.
	 *
	 * @param {string} [returnTo] Where Auth0 should send the fan back to.
	 * @return {Promise<void>}
	 */
	function logout( returnTo ) {
		return client.logout( {
			logoutParams: { returnTo: safeReturnTo( returnTo, config.homeUrl ) || config.homeUrl },
		} );
	}

	return { resolve, getAccessToken, getIdToken, login, logout, handleCallback };
}
